/**
 * <event-quick-filters>: chips de descuento y píldoras de tipo del toolbar de colección de evento
 * (snippets/event-quick-filters.liquid).
 *
 * Arma la URL con los parámetros de filtro de Shopify y la aplica con el FacetForm del theme
 * (assets/facets.js), igual que "quitar filtro": grilla, conteo, drawer y orden se actualizan por
 * AJAX. Cuando llega la respuesta (`facetUpdate`) se repinta con los conteos nuevos.
 * También marca `is-stuck` en su contenedor sticky para mostrar el borde inferior.
 */
if (!customElements.get('event-quick-filters')) {
  customElements.define(
    'event-quick-filters',
    class EventQuickFilters extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
        this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, (event) =>
          this.onFacetUpdate(event)
        );

        this.sticky = this.closest('[data-event-sticky]');
        if (this.sticky && !this.sticky.dataset.stuckWatch) {
          this.sticky.dataset.stuckWatch = 'true';
          this.onScroll = this.updateStuck.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.updateStuck();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.unsubscribe) this.unsubscribe();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      }

      onClick(event) {
        const chip = event.target.closest('[data-chip]');
        if (!chip || chip.disabled) return;

        const group = chip.closest('[data-param]');
        const param = group.dataset.param;
        const prefix = group.dataset.prefix || '';
        const value = chip.dataset.value;
        const url = new URL(window.location.href);
        const current = url.searchParams.getAll(param);
        let next;

        if (!value) {
          next = current.filter((v) => !v.startsWith(prefix));
        } else if (group.dataset.mode === 'single') {
          next = current.filter((v) => !v.startsWith(prefix));
          if (!chip.classList.contains('is-active')) next.push(value);
        } else {
          next = current.includes(value) ? current.filter((v) => v !== value) : current.concat(value);
        }

        url.searchParams.delete(param);
        next.forEach((v) => url.searchParams.append(param, v));
        url.searchParams.delete('page');

        this.select(group, chip, value);
        this.apply(url, event);
      }

      /* Respuesta inmediata al click; los conteos reales llegan con la respuesta del filtro. */
      select(group, chip, value) {
        const chips = group.querySelectorAll('[data-chip]');
        const all = group.querySelector('[data-chip][data-value=""]');
        if (!value || group.dataset.mode === 'single') {
          const wasActive = chip.classList.contains('is-active');
          chips.forEach((c) => c.classList.remove('is-active'));
          if (!(value && wasActive)) chip.classList.add('is-active');
          else if (all) all.classList.add('is-active');
        } else {
          chip.classList.toggle('is-active');
          if (all) all.classList.toggle('is-active', !group.querySelector('[data-chip]:not([data-value=""]).is-active'));
        }
      }

      apply(url, event) {
        const form = document.querySelector('form[is="facet-form"]');
        if (!form || typeof form.renderSection !== 'function') {
          window.location.href = url.toString();
          return;
        }
        url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
        form.renderSection(url.toString(), event);
      }

      onFacetUpdate({ responseText }) {
        const fresh = new DOMParser().parseFromString(responseText, 'text/html').getElementById(this.id);
        if (!fresh) return;
        this.innerHTML = fresh.innerHTML;
        EventSchedule.apply(this);
      }

      updateStuck() {
        if (this.stuckFrame) return;
        this.stuckFrame = requestAnimationFrame(() => {
          this.stuckFrame = null;
          const top = parseFloat(getComputedStyle(this.sticky).top);
          if (Number.isNaN(top)) return;
          this.sticky.classList.toggle('is-stuck', this.sticky.getBoundingClientRect().top <= top + 1 && window.scrollY > 0);
        });
      }
    }
  );
}

/**
 * Al bajar, la barra de filtros se esconde detrás del header para dejarle lugar a la grilla, y vuelve
 * apenas se sube (mobile y desktop). Solo se esconde si ya está pegada debajo del header (en su lugar
 * original se scrollea con la página). Marca `body.event-filters-hidden`; el movimiento está en
 * component-cyber-collection.css.
 */
(function () {
  const STICKY_SELECTOR = '.event-quick-bar';
  const HIDDEN_CLASS = 'event-filters-hidden';
  const TOLERANCE = 6; // px: ignora el temblor del scroll inercial y el rebote de iOS
  let lastY = Math.max(window.scrollY, 0);
  let frame = null;

  const isStuck = () =>
    Array.from(document.querySelectorAll(STICKY_SELECTOR)).some((el) => {
      if (!el.offsetParent) return false;
      const top = parseFloat(getComputedStyle(el).top);
      return !Number.isNaN(top) && el.getBoundingClientRect().top <= top + 1;
    });

  const update = () => {
    frame = null;
    const y = Math.max(window.scrollY, 0);
    if (Math.abs(y - lastY) < TOLERANCE) return;
    document.body.classList.toggle(HIDDEN_CLASS, y > lastY && isStuck());
    lastY = y;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!frame) frame = requestAnimationFrame(update);
    },
    { passive: true }
  );
})();
